import { Helmet } from 'react-helmet-async';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  ogType?: 'website' | 'article';
  ogImage?: string;
  article?: {
    publishedTime?: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  structuredData?: object | object[];
  noindex?: boolean;
}

const SITE_NAME = 'Automation Recipes';
const BASE_URL = 'https://automationrecipes.io';
const DEFAULT_OG_IMAGE = `${BASE_URL}/og-image.png`;
const DEFAULT_DESCRIPTION = 'Discover proven automation recipes to connect your existing tools. Step-by-step guides for Make, Zapier, and n8n integrations.';

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  ogType = 'website',
  ogImage = DEFAULT_OG_IMAGE,
  article,
  structuredData,
  noindex = false,
}: SEOProps) {
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} - Connect Your Business Tools with Make, Zapier & n8n`;
  const canonicalUrl = canonical ? `${BASE_URL}${canonical}` : undefined;

  // Handle both single object and array of structured data
  const structuredDataArray = structuredData
    ? Array.isArray(structuredData)
      ? structuredData
      : [structuredData]
    : [];

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={ogType} />
      {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
      <meta property="og:title" content={title || SITE_NAME} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Article specific meta */}
      {article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {article?.author && <meta property="article:author" content={article.author} />}
      {article?.section && <meta property="article:section" content={article.section} />}
      {article?.tags?.map((tag) => (
        <meta key={tag} property="article:tag" content={tag} />
      ))}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {canonicalUrl && <meta name="twitter:url" content={canonicalUrl} />}
      <meta name="twitter:title" content={title || SITE_NAME} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* Structured Data */}
      {structuredDataArray.map((data, index) => (
        <script key={index} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  );
}

// Helper to generate HowTo schema for recipes
export function generateRecipeSchema(recipe: {
  title: string;
  subtitle?: string | null;
  problem?: string | null;
  outcome?: string | null;
  steps?: string | null;
  tool_a?: { name: string } | null;
  tool_b?: { name: string } | null;
  slug: string;
  created_at: string;
  updated_at: string;
  last_verified_at?: string | null;
}) {
  const tools = [recipe.tool_a?.name, recipe.tool_b?.name].filter(Boolean);
  
  // Parse steps from markdown to array
  const stepsList = recipe.steps
    ? recipe.steps
        .split(/(?=^#{1,3}\s+|\d+\.\s+)/gm)
        .filter((s) => s.trim())
        .slice(0, 10) // Limit to 10 steps for schema
        .map((step, index) => ({
          '@type': 'HowToStep',
          position: index + 1,
          text: step.replace(/^#{1,3}\s+|\d+\.\s+/g, '').trim().substring(0, 200),
        }))
    : [];

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: recipe.title,
    description: recipe.subtitle || recipe.problem || `How to connect ${tools.join(' and ')}`,
    step: stepsList,
    tool: tools.map((name) => ({
      '@type': 'HowToTool',
      name,
    })),
    datePublished: recipe.created_at,
    dateModified: recipe.last_verified_at || recipe.updated_at,
  };

  // Article schema for broader discoverability
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'TechArticle',
    headline: recipe.title,
    description: recipe.subtitle || recipe.problem,
    datePublished: recipe.created_at,
    dateModified: recipe.last_verified_at || recipe.updated_at,
    author: {
      '@type': 'Organization',
      name: 'Automation Recipes',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Automation Recipes',
      url: 'https://automationrecipes.io',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://automationrecipes.io/recipes/${recipe.slug}`,
    },
  };

  return [howToSchema, articleSchema];
}

// Helper to generate FAQ schema
export function generateFAQSchema(
  faqs: Array<{ question: string; answer: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };
}

// Helper to generate Organization schema
export function generateOrganizationSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Automation Recipes',
    url: 'https://automationrecipes.io',
    logo: 'https://automationrecipes.io/logo.png',
    sameAs: [],
    description: 'Discover proven automation recipes to connect your existing tools. Step-by-step guides for Make, Zapier, and n8n integrations.',
  };
}

// Helper to generate Tool/Software schema
export function generateToolSchema(tool: {
  name: string;
  description?: string | null;
  slug: string;
  website_url?: string | null;
  category: string;
}) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: tool.name,
    description: tool.description,
    applicationCategory: tool.category,
    url: tool.website_url,
    operatingSystem: 'Web',
  };
}

// Helper to generate BreadcrumbList schema
export function generateBreadcrumbSchema(
  items: Array<{ name: string; url: string }>
) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://automationrecipes.io${item.url}`,
    })),
  };
}

// Website schema for homepage
export function generateWebsiteSchema() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Automation Recipes',
    url: 'https://automationrecipes.io',
    description: 'Discover proven automation recipes to connect your existing tools. Step-by-step guides for Make, Zapier, and n8n integrations.',
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://automationrecipes.io/recipes?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  };
}
